import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useWindowSize } from 'react-use'
import { css } from '@emotion/core'
import {
  listContainerStyle,
  listContentsWrapperStyle,
  listContentsStyle,
  HEADER_HEIGHT,
  MAIN_CONTAINER_WITH_HEADER_BAR_PADDING_VERTICAL,
  CONTAINER_WITH_HEADER_BUTTON_LIST_MARGIN,
  TAB_HEIGHT,
  GRAY_LABEL_HEIGHT,
  LIST_FOOTER_HEIGHT,
  headerBarLeftWrapperStyle,
} from '@/constants/layout'
import {
  settingHeaderContainerStyle,
  listWrapperStyle,
  listStyle,
  hideListStyle,
  deleteStyle,
} from '@/components/pages/setting/guest-room/room/layout'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { RoomRateTab } from '@/components/pages/setting/room-rate/tab'
import { SettingGuestRoomLabel } from '@/components/pages/setting/guest-room/room/_room/label'
import { Button } from '@/components/atoms/button'
import { EditFooter } from '@/components/organisms/edit-footer'
import { UnsavedChangesRouteGuard } from '@/components/molecules/unsaved-changes-route-guard'
import { PriceRankModal } from '@/components/pages/setting/room-rate/rank/_rank/modal/price-rank-modal'
import { createPriceRank, deletePriceRanks, updatePriceRank } from '@/apis/aipass'
import { usePriceRanks } from '@/hooks/use-price-ranks'
import { useSettingsEditGuard } from '@/hooks/use-settings-edit-guard'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { isStaffGuardCancelledError } from '@/libs/errors'
import { LoaderContextCreator } from '@/contexts/loader'
import { getCalendarDefaultLabelKey, getPriceRankColorHex } from '@/models/room-rate/price-rank'
import type { PriceRank, PriceRankCalendarDefaultType, PriceRankInput } from '@/models/room-rate/price-rank'

export const SettingRoomRateRank: React.FC = () => {
  const windows = useWindowSize()
  const { t } = useTranslation()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { errorHandler } = useErrorHandler()
  const { isSettingsReadOnly, guardSettingsEdit } = useSettingsEditGuard()
  const { ranks, ranksLoading, loadPriceRanks } = usePriceRanks()
  const [isLoading, setPageLoading] = useState(false)
  const [showFooter, setShowFooter] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [deleteRankIds, setDeleteRankIds] = useState<string[]>([])
  const [editRank, setEditRank] = useState<PriceRank | undefined>()
  const isSavingRef = useRef(false)

  const listItems = useMemo(
    () => [
      { label: t('Display order'), width: 96 },
      { label: t('Name (abbreviation)'), width: 224 },
      { label: t('Rate rank name'), width: 355 },
      { label: t('Rate calendar default'), width: 144 },
      { label: t('Color'), width: 50 },
    ],
    [t],
  )

  const maxExistingOrder = ranks.reduce((max, rank) => Math.max(max, rank.displayOrder), 0)
  const nextDisplayOrder = maxExistingOrder + 1

  /** 他ランクが使っている初期値区分（編集対象自身は除外） */
  const usedCalendarDefaultTypes = useMemo((): PriceRankCalendarDefaultType[] => {
    return ranks.filter(rank => rank.id !== editRank?.id && rank.calendarDefaultType !== 'none').map(rank => rank.calendarDefaultType)
  }, [ranks, editRank?.id])

  const reloadPriceRanks = async () => {
    setPageLoading(true)
    try {
      await loadPriceRanks()
    } catch (e) {
      errorHandler(e)
    } finally {
      setPageLoading(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setPageLoading(true)
      try {
        await loadPriceRanks()
      } catch (e) {
        if (!cancelled) {
          errorHandler(e)
        }
      } finally {
        if (!cancelled) {
          setPageLoading(false)
        }
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [loadPriceRanks])

  const onClickAddButton = () => {
    // 初回取得中は usedCalendarDefaultTypes が空になり、使用中区分が選べてしまう
    if (ranksLoading || !guardSettingsEdit()) return
    setEditRank(undefined)
    setShowCreateModal(true)
  }

  const onClickRow = (rank: PriceRank) => {
    if (ranksLoading || !guardSettingsEdit()) return
    setEditRank(rank)
    setShowEditModal(true)
  }

  const onClickDelete = (e: React.MouseEvent, rank: PriceRank) => {
    e.stopPropagation()
    if (!guardSettingsEdit()) return
    // 初期値区分付きは BE でも削除不可。事前に弾いて失敗キューを残さない
    if (rank.calendarDefaultType !== 'none') return
    setShowFooter(true)
    setDeleteRankIds(prev => (prev.includes(rank.id) ? prev : [...prev, rank.id]))
  }

  const onClickFooterSaveButton = async () => {
    if (!guardSettingsEdit()) return
    if (!deleteRankIds.length) {
      setShowFooter(false)
      return
    }
    if (!window.confirm(t('Are you sure you want to delete it'))) {
      setDeleteRankIds([])
      setShowFooter(false)
      return
    }

    try {
      setIsLoading(true)
      await deletePriceRanks(deleteRankIds)
      setDeleteRankIds([])
      setShowFooter(false)
      await reloadPriceRanks()
    } catch (e) {
      if (!isStaffGuardCancelledError(e)) {
        errorHandler(e)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onClickFooterCancelButton = () => {
    setShowFooter(false)
    setDeleteRankIds([])
  }

  const onClickModalCancel = () => {
    setShowCreateModal(false)
    setShowEditModal(false)
    setEditRank(undefined)
  }

  const onClickModalSave = async (values: PriceRankInput) => {
    if (!guardSettingsEdit()) return
    if (isSavingRef.current) return
    isSavingRef.current = true
    try {
      setIsLoading(true)
      if (editRank) {
        await updatePriceRank(editRank.id, values)
      } else {
        await createPriceRank(values)
      }
      await reloadPriceRanks()
      onClickModalCancel()
    } catch (e) {
      if (!isStaffGuardCancelledError(e)) {
        errorHandler(e)
      }
    } finally {
      isSavingRef.current = false
      setIsLoading(false)
    }
  }

  const calendarDefaultLabel = (rank: PriceRank) => {
    const type = rank.calendarDefaultType
    if (!type || type === 'none') return '-'
    const labelKey = getCalendarDefaultLabelKey(type)
    return labelKey ? t(labelKey) : '-'
  }

  const footerContent = showFooter ? <EditFooter onSave={onClickFooterSaveButton} onCancel={onClickFooterCancelButton} /> : undefined

  return (
    <>
      {showCreateModal && !ranksLoading && (
        <PriceRankModal
          title={t('Create rate rank')}
          nextDisplayOrder={nextDisplayOrder}
          usedCalendarDefaultTypes={usedCalendarDefaultTypes}
          onClickCancel={onClickModalCancel}
          onClickSave={onClickModalSave}
        />
      )}
      {showEditModal && !ranksLoading && (
        <PriceRankModal
          title={t('Edit rate rank')}
          editRank={editRank}
          nextDisplayOrder={nextDisplayOrder}
          usedCalendarDefaultTypes={usedCalendarDefaultTypes}
          onClickCancel={onClickModalCancel}
          onClickSave={onClickModalSave}
        />
      )}
      <SettingsLayout loading={isLoading} footerContent={footerContent}>
        <>
          <UnsavedChangesRouteGuard hasUnsaved={deleteRankIds.length > 0} unsavedLabels={[{ key: 'priceRank', label: t('Rate rank') }]} />
          <div css={settingHeaderContainerStyle}>
            <RoomRateTab currentTab="rank" />
          </div>
          <div css={listContainerStyle}>
            <div css={editButtonWrapperStyle}>
              <div css={headerBarLeftWrapperStyle}>
                <Button
                  buttonType={isSettingsReadOnly || ranksLoading ? 5 : 1}
                  width={107}
                  height={32}
                  marginRight={16}
                  fontSize={12}
                  isDisabled={isSettingsReadOnly || ranksLoading}
                  icon={require('@/static/images/mobile_request/add.svg')}
                  onClick={onClickAddButton}
                >
                  {t('Add new')}
                </Button>
              </div>
            </div>
            <SettingGuestRoomLabel listItems={listItems} />
            <div
              css={listContentsWrapperStyle}
              style={{
                maxHeight:
                  windows.height -
                  HEADER_HEIGHT -
                  MAIN_CONTAINER_WITH_HEADER_BAR_PADDING_VERTICAL -
                  CONTAINER_WITH_HEADER_BUTTON_LIST_MARGIN -
                  TAB_HEIGHT -
                  GRAY_LABEL_HEIGHT -
                  (showFooter ? LIST_FOOTER_HEIGHT : 0),
              }}
            >
              <div css={listContentsStyle}>
                {ranks.map(rank => {
                  if (deleteRankIds.includes(rank.id)) {
                    return <div key={rank.id} css={hideListStyle} />
                  }
                  const canDelete = rank.calendarDefaultType === 'none'
                  return (
                    <div key={rank.id} css={listWrapperStyle}>
                      <div css={listStyle} onClick={() => onClickRow(rank)}>
                        <div style={{ width: listItems[0].width }}>{rank.displayOrder}</div>
                        <div style={{ width: listItems[1].width }}>{rank.shortName}</div>
                        <div style={{ width: listItems[2].width }}>{rank.name}</div>
                        <div style={{ width: listItems[3].width }}>{calendarDefaultLabel(rank)}</div>
                        <div style={{ width: listItems[4].width }}>
                          <span css={colorChipStyle} style={{ background: getPriceRankColorHex(rank.color) }} />
                        </div>
                      </div>
                      <img
                        css={[deleteStyle, !canDelete && deleteDisabledStyle]}
                        src={require('@/static/images/room_delete_gold.svg')}
                        onClick={e => onClickDelete(e, rank)}
                      />
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </>
      </SettingsLayout>
    </>
  )
}

const editButtonWrapperStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '24px 32px 19px',
  backgroundColor: '#fff',
})

const colorChipStyle = css({
  display: 'inline-block',
  width: 24,
  height: 24,
  borderRadius: 4,
})

const deleteDisabledStyle = css({
  opacity: 0.35,
  pointerEvents: 'none',
  cursor: 'default',
})
